import {
	KeyBoardListNavigationContext,
	useKeyboardListNavigation,
	useSynchronizedRef
} from "client/Hooks";
import { EditorView } from "prosemirror-view";
import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { Command, EditorState } from "prosemirror-state";
import { Key, Rect } from "client/Misc/commonTypes";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { isNil, isNotNil } from "client/Misc/utils";
import { ProsemirrorViewHandle } from "../ProsemirrorView/ProsemirrorView";

export type MentionInputSuggestion = {
	data: string;
	text: string;
};

export type MentionInputSuggestionBoxProps = {
	mentionType: string;
	searchTerm: string;
	onSelectItem(suggestion: MentionInputSuggestion): void;
	onEmptyStatusChange(searchTerm: string, isEmpty: boolean): void;
	onClose(): void;
	trackingId: string;
};

export type MentionInputSuggestionBoxHandle = {
	getTopSuggestion(): MentionInputSuggestion | null;
};

export type UseMentionsHandlers = {
	textInputHandler: (view: EditorView, from: number, to: number, text: string) => boolean;
	keyDownHandler: (view: EditorView, event: KeyboardEvent) => boolean;
	suggestionBox: ReactElement | null;
};

export type UseMentionsOptions = {
	mentionTypes: readonly string[];
	editorState: EditorState;
	viewRef: ReadonlyRefObject<ProsemirrorViewHandle | null>;
	insertSuggestion: (
		from: number,
		to: number,
		type: string,
		suggestion: MentionInputSuggestion
	) => Command | undefined;
	renderSuggestionBox(
		props: MentionInputSuggestionBoxProps,
		ref: Ref<MentionInputSuggestionBoxHandle>,
		rect: Rect
	): ReactElement | null;
	onInsertSuggestion?(
		suggestion: MentionInputSuggestion,
		mentionType: string
	): void;
	isMentionTypeSupportedBySchema?: (type: string) => boolean;
	trackingId: string;
};

export function useMentions(options: UseMentionsOptions): UseMentionsHandlers {
	const {
		mentionTypes,
		editorState,
		viewRef,
		renderSuggestionBox: onRenderSuggestionBox,
		insertSuggestion,
		onInsertSuggestion,
		isMentionTypeSupportedBySchema,
		trackingId
	} = options;

	const [mentionMode, setMentionMode] = useState<MentionMode>({ isActive: false });

	const suggestionBoxRef: RefObject<MentionInputSuggestionBoxHandle | null> = useRef<MentionInputSuggestionBoxHandle>(null);

	const textInputHandler = useCallback(
		(
			view: EditorView,
			from: number,
			to: number,
			text: string
		): boolean => {
			if (!mentionTypes.includes(text)) {
				return false;
			}

			if (isMentionTypeSupportedBySchema && !isMentionTypeSupportedBySchema(text)) {
				console.error(`Mention type '${text}' is not supported by schema.`);
				return false;
			}

			// NOTE: `coordsAtPos` method returns a 0 width rectangle of the current position  for the
			// proper rendering of the drop over with suggestions we need to calculate a rectangle around
			// the character, so we retrieve position before and after it and use it for the calculation.
			let positionBeforeChar = view.coordsAtPos(from, -1);
			const positionAfterChar = view.coordsAtPos(from, 1);

			// We need to ensure that the positions are properly aligned with each other, otherwise we
			// will produce an invalid rectangle. Eg they are on the same line and horizontally position
			// after character is bigger then the position before character
			if (positionAfterChar.top !== positionBeforeChar.top
				|| positionAfterChar.bottom !== positionBeforeChar.bottom
				|| positionAfterChar.right < positionBeforeChar.left
			) {
				positionBeforeChar = positionAfterChar;
			}

			const {
				left,
				top,
				bottom
			} = positionBeforeChar;

			const {
				right
			} = positionAfterChar;

			setMentionMode({
				isActive: true,
				mentionType: text,
				from: to,
				searchTerm: "",
				rect: {
					left,
					top,
					right,
					bottom
				}
			});

			return false;
		},
		[mentionTypes, isMentionTypeSupportedBySchema]
	);

	const mentionModeRef: ReadonlyRefObject<MentionMode> = useSynchronizedRef(mentionMode);

	const insertSuggestionHandler = useCallback(
		(view: EditorView, suggestion: MentionInputSuggestion): void => {
			const {
				current: mentionMode
			} = mentionModeRef;

			if (!mentionMode.isActive) {
				throw new Error("Mention mode is not active");
			}

			insertSuggestion(
				mentionMode.from,
				view.state
					.selection
					.to,
				mentionMode.mentionType,
				suggestion
			)
				?.(view.state, view.dispatch);

			onInsertSuggestion?.(suggestion, mentionMode.mentionType);
		},
		[
			insertSuggestion,
			mentionModeRef,
			onInsertSuggestion
		]
	);

	const cancelMentionModeHandler = useCallback(
		(): void => {
			setMentionMode({ isActive: false });
		},
		[]
	);

	const focusInputHandler = useCallback(
		(): void => {
			viewRef.current
				?.focus();
		},
		[viewRef]
	);

	const {
		contextData,
		focusFirstItemHandler
	} = useKeyboardListNavigation({
		onFocusInput: focusInputHandler,
		onCancel: cancelMentionModeHandler,
		shouldCancelDirectlyFromItems: true
	});

	const keyDownHandler = useCallback(
		(view: EditorView, event: KeyboardEvent): boolean => {
			const {
				current: mentionMode
			} = mentionModeRef;

			if (!mentionMode.isActive) {
				return false;
			}

			const key: Key | null = tryResolveKeyboardKey(event);

			switch (key) {
				case Key.Enter:
					const topSuggestion: MentionInputSuggestion | nil = suggestionBoxRef.current
						?.getTopSuggestion();

					if (isNotNil(topSuggestion)) {
						insertSuggestionHandler(view, topSuggestion);
					}
					cancelMentionModeHandler();

					return true;
				case Key.ArrowDown:
					focusFirstItemHandler();
					return true;
				case Key.Escape:
				case Key.Tab:
					cancelMentionModeHandler();
					return true;
				default:
					return false;
			}
		},
		[
			mentionModeRef,
			cancelMentionModeHandler,
			focusFirstItemHandler,
			insertSuggestionHandler
		]
	);

	const selectItemHandler = useCallback(
		(suggestion: MentionInputSuggestion): void => {
			const viewObject: EditorView | nil = viewRef.current
				?.getProsemirrorViewObject();

			if (!mentionMode.isActive || !viewObject) {
				return;
			}

			insertSuggestionHandler(viewObject, suggestion);

			viewObject.focus();
		},
		[
			viewRef,
			mentionMode.isActive,
			insertSuggestionHandler
		]
	);

	const emptyStatusChangeHandler = useCallback(
		(searchTerm: string, isEmpty: boolean): void => {
			setMentionMode(
				(mentionMode) => {
					if (!mentionMode.isActive) {
						return mentionMode;
					}

					if (isEmpty) {
						if (isNotNil(mentionMode.noResultsFrom)) {
							return mentionMode;
						}

						return {
							...mentionMode,
							noResultsFrom: mentionMode.from + searchTerm.length
						};
					} else {
						if (isNil(mentionMode.noResultsFrom)) {
							return mentionMode;
						}

						return {
							...mentionMode,
							noResultsFrom: undefined
						};
					}
				}
			);
		},
		[]
	);

	useEffect(
		(): void => {
			setMentionMode(
				(mentionMode) => {
					if (!mentionMode.isActive) {
						return mentionMode;
					}

					const {
						to
					} = editorState.selection;

					if (to < mentionMode.from) {
						return {
							isActive: false
						};
					}

					if (isNotNil(mentionMode.noResultsFrom)) {
						if (to - mentionMode.noResultsFrom > 7) {
							return {
								isActive: false
							};
						}
					}

					let newSearchTerm: string = editorState.doc
						.textBetween(mentionMode.from, to);

					if (!newSearchTerm.startsWith(mentionMode.mentionType)) {
						return {
							isActive: false
						};
					}

					newSearchTerm = newSearchTerm.slice(
						mentionMode.mentionType
							.length
					);

					return {
						...mentionMode,
						searchTerm: newSearchTerm
					};
				}
			);
		},
		[editorState]
	);

	const renderSuggestionBox = (): ReactElement | null => {
		if (!mentionMode.isActive) {
			return null;
		}

		const renderedSuggestionBox: ReactElement | null = onRenderSuggestionBox(
			{
				mentionType: mentionMode.mentionType,
				searchTerm: mentionMode.searchTerm,
				onSelectItem: selectItemHandler,
				onEmptyStatusChange: emptyStatusChangeHandler,
				onClose: cancelMentionModeHandler,
				trackingId: trackingId
			},
			suggestionBoxRef,
			mentionMode.rect
		);

		if (!renderedSuggestionBox) {
			return null;
		}

		return (
			<KeyBoardListNavigationContext.Provider value={contextData}>
				{renderedSuggestionBox}
			</KeyBoardListNavigationContext.Provider>
		);
	};

	return {
		textInputHandler,
		keyDownHandler,
		suggestionBox: renderSuggestionBox()
	};
};

type MentionMode = {
	isActive: false;
} | {
	isActive: true;
	mentionType: string;
	from: number;
	searchTerm: string;
	noResultsFrom?: number;
	rect: Rect;
};